<script>
import VuetifyTextClass from '@/shared/constants/core/VuetifyTextClass'
import VuetifyColourClass from '@/shared/constants/core/VuetifyColourClass'
import SimpleTableItem from '@/models/general/simpleTableItem'
import BaseButton from '@/components/_base-button.vue'

export default {
  components: {
    BaseButton,
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      // Parents table items
      type: Array,
      default: () => [],
      validator: (items) =>
        items.every((item) => item instanceof SimpleTableItem),
    },
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
      validator: (items) =>
        items.every((item) => item instanceof SimpleTableItem),
    },
    removable: {
      type: Boolean,
      default: false,
    },
    disableRemove: {
      type: Boolean,
      default: false,
    },
    showIds: {
      type: Boolean,
      default: false,
    },
    excludedKeys: {
      type: Array,
      default: () => [],
    },
    allowedKeys: {
      type: Array,
      default: () => [],
    },
    height: {
      type: String,
      default: '',
    },
    centeredHeaders: {
      type: Array,
      default: () => [],
    },
    dense: {
      type: Boolean,
      default: false,
    },
    removeIconSize: {
      type: String,
      default: 'xxl',
    },
    softRemovalKeys: {
      // hides strike through for the specified columns
      type: Array,
      default: () => [],
    },
    removeIcon: {
      type: String,
      default: 'mdi-delete-outline',
    },
    removeHeader: {
      type: String,
      default: 'Remove',
    },
  },
  data: () => ({
    ...VuetifyTextClass,
    text: VuetifyColourClass.text,
  }),
  computed: {
    activeItems() {
      return this.items.filter((item) => !item.removed)
    },
    useModeledItems() {
      return this.value?.length || this.removable
    },
    tableItems() {
      return this.useModeledItems ? this.value : this.items
    },
    allHeaders() {
      return [...this.headers, ...this.extraColumns]
    },
    allExcludedKeys() {
      const allExcludedKeys = [...this.excludedKeys]

      if (!this.showIds) allExcludedKeys.push('id')

      if (!this.removable) allExcludedKeys.push('removed')

      return allExcludedKeys
    },
    itemKeys() {
      if (this.allowedKeys.length) {
        return [...this.allowedKeys, ...this.extraColumns]
      }
      return [
        ...new Set(
          this.items.flatMap((item) =>
            Object.keys(item).filter(
              (key) => !this.allExcludedKeys.includes(key)
            )
          )
        ),
        ...this.extraColumns,
      ]
    },
    extraColumns() {
      const columns = []

      if (this.removable) columns.push('remove')

      return columns
    },
  },

  methods: {
    toggleRemoved(id) {
      const updatedItem = this.value.find((item) => item.id === id)

      if (updatedItem) {
        updatedItem.removed = !updatedItem.removed
      }

      this.$emit('input', this.value)
    },
  },
}
</script>

<template>
  <v-simple-table
    v-bind="{ ...$attrs }"
    class="base-simple-table"
    :class="{ 'base-simple-table--dense': dense }"
    :style="{ maxHeight: height }"
  >
    <template v-slot>
      <thead class="simple-table-head">
        <tr>
          <th
            v-for="header in allHeaders"
            :key="header"
            class="text-left text-capitalize"
            :class="[
              text.caption,
              {
                'text-center':
                  header === 'remove' ||
                  centeredHeaders.map(({ title }) => title).includes(header),
              },
            ]"
          >
            <slot :name="`${header}-header`" :header="header">
              {{ header === 'remove' ? removeHeader : header }}
            </slot>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in tableItems" :key="index">
          <td
            v-for="key in itemKeys"
            :key="key"
            :class="[
              smallBody,
              { 'd-flex justify-center align-center': key === 'remove' },
            ]"
          >
            <template v-if="key === 'remove'">
              <transition
                :name="`slide-${item.removed ? 'right' : 'left'}-icon`"
                mode="out-in"
              >
                <BaseButton
                  v-if="item.removed"
                  key="undo"
                  text
                  class="mx-auto"
                  :class="[smallBody]"
                  color="grey500"
                  hover-color="grey600"
                  width="97"
                  @click="toggleRemoved(item.id)"
                  >{{ $t('general.undo') }}</BaseButton
                >
                <BaseButton
                  v-else
                  key="remove"
                  fab
                  :size="removeIconSize"
                  :disabled="disableRemove"
                  :icon="removeIcon"
                  text
                  icon-hover-color="error"
                  hover-color="error lighten-1"
                  class="mx-auto"
                  icon-color="grey"
                  width="97"
                  @click="toggleRemoved(item.id)"
                />
              </transition>
            </template>
            <template v-else>
              <div
                class="cell"
                :class="{
                  removed: item.removed && !softRemovalKeys.includes(key),
                  'mx-auto': centeredHeaders
                    .map(({ value }) => value)
                    .includes(key),
                }"
              >
                <slot :name="`${key}-cell`" :item="item">
                  {{ item[key] }}
                </slot>
              </div>
            </template>
          </td>
        </tr>
      </tbody>
    </template>
  </v-simple-table>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/_vue-transitions.scss';

.base-simple-table {
  border: 1px solid $color-grey-300;
  border-radius: 16px;
}

.base-simple-table ::v-deep .v-data-table__wrapper {
  height: auto !important;
  max-height: inherit !important;
  overflow-y: auto;
}

.base-simple-table thead th {
  background-color: #fafafa !important;

  &:first-child {
    border-radius: 16px 0 0 0;
  }
  &:last-child {
    border-radius: 0 16px 0 0;
  }
}

.base-simple-table ::v-deep td {
  min-height: 64px !important;
}

.base-simple-table--dense ::v-deep td {
  min-height: 48px !important;
}

.simple-table-head {
  color: $color-grey-600 !important;
  background-color: #fafafa;
}

.cell {
  position: relative;
  width: fit-content;
}

.cell::after {
  position: absolute;
  bottom: 50%;
  left: 0;
  width: 0;
  height: 2px;
  content: '';
  background: $color-grey-600;
  opacity: 0.8;
  transition: width 0.2s ease;
}

.removed.cell::after {
  width: 110%;
  margin: 0 -5%;
}
</style>
